import * as Sentry from '@sentry/nextjs'

import { Button } from '@lockerpm/design'
import { DEFAULT_PASSWORD_NOTION_TABLE } from '@/config/constants'
import DefaultLayout from '@/layouts/default'
import ENDPOINT from '@/config/endpoint'
import ForgotPassword from '@/components/pages/password-generator/ForgotPassword'
import FrequentlyQuestion from '@/components/pages/password-generator/FrequentlyQuestion'
import { ParsePasswordGeneratorTitle } from '@/utils/common'
import PasswordGeneratorBanner from '@/components/pages/password-generator/Banner'
import PasswordUseCase from '@/components/pages/password-generator/UseCase'
import Review from '@/components/pages/password-generator/Review'
import SetStrongPassword from '@/components/pages/password-generator/SetStrongPassword'
import StrongPassword from '@/components/pages/password-generator/StrongPassword'
import { fetchPasswordGeneratorApp } from '@/services/resource'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'

export async function getServerSideProps({ locale, params }) {
  try {
    const { slug } = params
    if (!slug) return
    let seoTitle = `Locker Password Generator - ${ParsePasswordGeneratorTitle(slug)}`
    let bannerTitle = `${ParsePasswordGeneratorTitle(slug)}`
    let seoDescription =
      'Sử dụng công cụ tạo mật khẩu mạnh, duy nhất, và hoàn toàn ngẫu nhiên của Locker để giúp bạn bảo mật các tài khoản trực tuyến an toàn hơn.'
    if (slug && slug.includes('app-')) {
      const response = await fetchPasswordGeneratorApp()
      const listApp = response.data[DEFAULT_PASSWORD_NOTION_TABLE].collection.data.map((item) => {
        return {
          ...item,
          domain: item.Domain[0],
          name: item.Name[0],
          alias: item.Alias[0],
          slug: `app-${item.Alias[0]}`,
        }
      })
      const currentApp = listApp.find((item) => item.slug === slug)
      if (!!currentApp) {
        bannerTitle = `${ParsePasswordGeneratorTitle(slug, currentApp)}`
        seoTitle = `Locker Password Generator - ${ParsePasswordGeneratorTitle(slug, currentApp)}`
        seoDescription = currentApp.Desc[0][0]
      }
    }
    return {
      props: {
        bannerTitle,
        seoTitle,
        seoDescription,
        locale,
        ...(await serverSideTranslations(locale)),
      },
    }
  } catch (error) {
    Sentry.captureMessage(error)
    return {
      props: {
        bannerTitle: 'Công cụ tạo mật khẩu mạnh ngẫu nhiên',
        seoTitle: 'Locker Password Generator - Công cụ tạo mật khẩu mạnh ngẫu nhiên',
        seoDescription:
          'Sử dụng công cụ tạo mật khẩu mạnh, duy nhất, và hoàn toàn ngẫu nhiên của Locker để giúp bạn bảo mật các tài khoản trực tuyến an toàn hơn.',
        locale,
        ...(await serverSideTranslations(locale)),
      },
    }
  }
}

export default function LockerPasswordGenerator({ bannerTitle, seoTitle, seoDescription }) {
  const { t } = useTranslation('home')
  return (
    <DefaultLayout title={seoTitle || t('seo.title')} desc={seoDescription || t('seo.desc')}>
      <PasswordGeneratorBanner bannerTitle={bannerTitle} bannerDescription={seoDescription} />
      <section className="py-12">
        <div className="max-w-[1280px] xl:px-20 lg:px-10 px-5 mx-auto bg-gray-50 rounded-2xl">
          <div className="lg:px-20 lg:py-16 px-0 py-10 flex-col md:flex-row flex items-center justify-between gap-10">
            <div className="space-y-6 max-w-[645px]">
              <h2 className="text-display-xs lg:text-display-sm text-heading font-semibold">
                Bạn cần một công cụ tạo mật khẩu khó đoán để đảm bảo an toàn cho tài khoản?
              </h2>
              <p className="text-heading-desctiption text-sm md:text-md lg:text-lg">
                Locker Password Manger - công cụ tự động tạo, lưu trữ, quản lý và đồng bộ mật khẩu
                trên mọi thiết bị.
              </p>
            </div>
            <Link href={`${ENDPOINT.ID_PM_REGISTER}`} target="_blank">
              <Button type="primary" size="large" className="h-[60px] px-6 rounded-lg">
                Dùng thử miễn phí
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <PasswordUseCase />
      <StrongPassword />
      <ForgotPassword />
      <SetStrongPassword />
      <section className="py-12">
        <div className="max-w-[1280px] xl:px-20 lg:px-10 px-5 mx-auto bg-gray-50 rounded-2xl">
          <div className="lg:px-20 lg:py-16 px-0 py-10 md:flex-row flex-col flex items-center justify-between gap-10">
            <div className="w-full max-w-full md:max-w-[645px]">
              <h2 className="text-display-xs lg:text-display-sm text-heading font-semibold mb-0">
                Trải nghiệm internet an toàn và liền mạch với Locker Password Manager
              </h2>
            </div>
            <Link href={'/passwords/download'} target="_blank" rel="noopener">
              <Button type="primary" size="large" className="h-[60px] px-6 rounded-lg">
                Tải miễn phí
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <Review />
      <FrequentlyQuestion />
    </DefaultLayout>
  )
}
